import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Journeys.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL = '/rest/sfmc/journeys';

/* ─── Helpers ─── */

function formatNumber(num) {
    if (num == null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeStatus(raw) {
    if (!raw) return 'Draft';
    const s = raw.toLowerCase();
    if (s === 'running' || s === 'active' || s.includes('active')) return 'Active';
    if (s === 'stopped' || s === 'paused' || s.includes('pause')) return 'Paused';
    if (s === 'draft') return 'Draft';
    return raw;
}

function statusAccent(status) {
    if (status === 'Active') return 'accent-active';
    if (status === 'Paused') return 'accent-paused';
    return 'accent-draft';
}

function statusBadge(status) {
    if (status === 'Active') return { cls: 'status-badge-active', dot: 'status-dot-active' };
    if (status === 'Paused') return { cls: 'status-badge-paused', dot: 'status-dot-paused' };
    return { cls: 'status-badge-draft', dot: 'status-dot-draft' };
}

/* ─── Journey Card ─── */
function JourneyCard({ journey, onClick }) {
    const status   = normalizeStatus(journey.status);
    const accent   = statusAccent(status);
    const badge    = statusBadge(status);

    const entered    = journey.contactsEnteredCount || journey.entered || journey.stats?.contactsEnteredCount || 0;
    const completed  = journey.completed  || journey.contactsCompleted || journey.stats?.contactsExitedCount || 0;
    const inProgress = entered - completed > 0 ? entered - completed : 0;
    const completionRate = entered > 0 ? ((completed / entered) * 100).toFixed(1) : 0;

    const emailsSent = journey.emailSent  || journey.emailsSent || journey.stats?.emailsSent || 0;
    const openRate   = journey.openRate   || journey.stats?.openRate  || null;
    const goalMet    = journey.goals      || journey.goalMet   || journey.stats?.goalMet || null;

    const version    = journey.version ? `v${journey.version}` : '';
    const modified   = journey.modifiedDate || journey.lastUpdated || journey.modifiedAt;
    const subtitle   = [version, modified ? `Modified ${formatDate(modified)}` : ''].filter(Boolean).join(' \u00b7 ');

    const dataExtensions = journey.dataExtensions || journey.entrySourceDEs || [];
    const automations    = journey.automations    || journey.relatedAutomations || [];

    return (
        <div className="journey-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className={`accent-stripe ${accent}`} />
            <div style={{ paddingLeft: 14 }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <h3 className="journey-title">{journey.name || 'Unnamed Journey'}</h3>
                        {subtitle && <p className="journey-subtitle">{subtitle}</p>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className={badge.cls}>
                            <span className={badge.dot} />
                            {status}
                        </span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </div>
                </div>

                {/* DE & Automation Pills */}
                {(dataExtensions.length > 0 || automations.length > 0) && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {dataExtensions.map((de, i) => (
                            <span key={de.id || i} className="de-pill">
                                <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" />
                                </svg>
                                <span>{de.name || de}</span>
                                {de.rowCount != null && <span className="pill-count">{formatNumber(de.rowCount)}</span>}
                            </span>
                        ))}
                        {automations.map((auto, i) => (
                            <span key={auto.id || i} className="auto-pill">
                                <span className="status-dot-pill" style={{ background: '#3B82F6' }} />
                                <svg className="pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                </svg>
                                <span>{auto.name || auto}</span>
                                {auto.stepCount != null && <span className="pill-count">{auto.stepCount} steps</span>}
                            </span>
                        ))}
                    </div>
                )}

                {/* KPI Grid */}
                <div className="journey-kpi-grid">
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">Entered</p>
                        <p className="journey-kpi-value" style={{ color: '#6366F1' }}>{formatNumber(entered)}</p>
                    </div>
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">Completed</p>
                        <p className="journey-kpi-value" style={{ color: '#10B981' }}>{formatNumber(completed)}</p>
                        {entered > 0 && <p className="journey-kpi-sub">{completionRate}%</p>}
                    </div>
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">In Progress</p>
                        <p className="journey-kpi-value" style={{ color: '#3B82F6' }}>{formatNumber(inProgress)}</p>
                    </div>
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">Emails Sent</p>
                        <p className="journey-kpi-value" style={{ color: '#8B5CF6' }}>{formatNumber(emailsSent)}</p>
                    </div>
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">Open Rate</p>
                        <p className="journey-kpi-value" style={{ color: '#F59E0B' }}>{openRate != null ? `${openRate}%` : 'N/A'}</p>
                    </div>
                    <div className="journey-kpi-item">
                        <p className="journey-kpi-label">Goal</p>
                        <p className="journey-kpi-value" style={{ color: goalMet >= 80 ? '#10B981' : '#F59E0B' }}>{goalMet != null ? `${goalMet}%` : 'N/A'}</p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="progress-bar-custom">
                    <div className="progress-bar-fill" style={{ width: `${Math.min(completionRate, 100)}%`, background: '#10B981' }} />
                </div>
            </div>
        </div>
    );
}

/* ─── Main Journeys Page ─── */
function Journeys() {
    usePageTitle('Journeys');
    const navigate = useNavigate();
    const [journeys,     setJourneys]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState(null);
    const [searchQuery,  setSearchQuery]  = useState('');

    useEffect(() => {
        axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const raw  = Array.isArray(data) ? data : (data.journeys || data.items || data.data || []);
                setJourneys(raw);
            })
            .catch(err => {
                const msg = err.response?.data?.message || err.message;
                setError(msg);
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = journeys.filter(j => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (j.name && j.name.toLowerCase().includes(q))
            || (j.description && j.description.toLowerCase().includes(q))
            || (j.status && j.status.toLowerCase().includes(q));
    });

    // Compute KPI totals
    const totalJourneys  = journeys.length;
    const activeCount    = journeys.filter(j => normalizeStatus(j.status) === 'Active').length;
    const totalEntered   = journeys.reduce((s, j) => s + (j.contactsEnteredCount || j.entered || j.stats?.contactsEnteredCount || 0), 0);
    const totalEmails    = journeys.reduce((s, j) => s + (j.emailSent || j.emailsSent || j.stats?.emailsSent || 0), 0);
    const openRates      = journeys.map(j => j.openRate || j.stats?.openRate).filter(r => r != null);
    const avgOpenRate    = openRates.length > 0 ? (openRates.reduce((a, b) => a + b, 0) / openRates.length).toFixed(1) : null;

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header" style={{ marginBottom: 16 }}>
                    <h1>Journey Audit</h1>
                    <p>Full visibility across all Marketing Cloud journeys</p>
                </div>

                {loading && <div className="text-center py-5"><p>Loading journeys...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load journeys: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                        <div className="col-6 col-md-3">
                            <div className="kpi-card d-flex align-items-start gap-3">
                                <div className="kpi-icon" style={{ background: '#6366F114' }}>◈</div>
                                <div>
                                    <p className="kpi-label">Total Journeys</p>
                                    <p className="kpi-value">{totalJourneys}</p>
                                    <p className="kpi-subtext">{activeCount} active</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="kpi-card d-flex align-items-start gap-3">
                                <div className="kpi-icon" style={{ background: '#10B98114' }}>👥</div>
                                <div>
                                    <p className="kpi-label">Contacts Entered</p>
                                    <p className="kpi-value">{formatNumber(totalEntered)}</p>
                                    <p className="kpi-subtext">across all journeys</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="kpi-card d-flex align-items-start gap-3">
                                <div className="kpi-icon" style={{ background: '#8B5CF614' }}>✉</div>
                                <div>
                                    <p className="kpi-label">Emails Sent</p>
                                    <p className="kpi-value">{formatNumber(totalEmails)}</p>
                                    <p className="kpi-subtext">all time</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="kpi-card d-flex align-items-start gap-3">
                                <div className="kpi-icon" style={{ background: '#F59E0B14' }}>👁</div>
                                <div>
                                    <p className="kpi-label">Avg Open Rate</p>
                                    <p className="kpi-value">{avgOpenRate != null ? `${avgOpenRate}%` : 'N/A'}</p>
                                    <p className="kpi-subtext">across all sends</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="search-wrapper mb-3">
                        <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search journeys..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* Results count */}
                    <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 12 }}>
                        Showing <strong style={{ color: '#0F172A' }}>{filtered.length}</strong> of {journeys.length} journeys
                    </p>

                    {/* Journey Cards */}
                    {filtered.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                            {filtered.map((j, i) => (
                                <JourneyCard key={j.id || j.journeyId || i} journey={j} onClick={() => navigate(`/journeys/${j.id || j.journeyId}`)} />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-icon">🔭</div>
                            <h3 className="empty-state-title">No journeys found</h3>
                            <p className="empty-state-text">Try a different search term.</p>
                        </div>
                    )}
                </>}
            </div>
        </>
    );
}

export default Journeys;
